import { useEffect } from 'react'
import { Route, Navigate } from 'react-router-dom'
import rootStore from './store'
import UserNotActivePage from './pages/UserNotActivePage'
import UserOutOfCreditsPage from './pages/UserOutOfCreditsPage'
import NotFoundPage from '@/ui/error/NotFound'
import LinearProgress from '@/ui/LinearProgress'
import { User } from '@/features/auth/types'
import { KnowledgeBaseOAuthCallback } from './pages/KnowledgeBaseOAuthCallback'
import { KnowledgeBaseFormPage } from './pages/KnowledgeBaseFormPage'
import { KnowledgeBaseDetailPage } from './pages/KnowledgeBaseDetailPage'
import SpaceDetailPage from './pages/SpaceDetailPage'
import ImpersonationPage from './pages/ImpersonationPage'
import { ErpEmbed } from './pages/ErpEmbed'
import { ERP_APPS } from '@/features/erp/config'
import OrganizationLayout from '@/ui/system/OrganizationLayout'
import KnowledgeLayout from '@/ui/system/KnowledgeLayout'

export const useAuthRoutes = (user: User | null) => {
  const { organizations } = rootStore

  const userRole = user?.role
  const isUserActive = user?.active
  const chatTokensLeft = user?.chatTokensLeft ?? 0

  const isUserAdmin = userRole === 'admin'
  const isUserOrgManager = userRole === 'org-manager'

  useEffect(() => {
    if (!user) {
      return
    }

    if (isUserOrgManager) {
      organizations.fetchPlan()
    }
  }, [user])

  if (!user) {
    return null
  }

  const orgPlanNotLoaded =
    isUserOrgManager &&
    (organizations.initialLoadingPlan || !organizations.planInfo)

  if (orgPlanNotLoaded) {
    return (
      <Route
        path="*"
        element={
          <div className="top-loading-bar">
            <LinearProgress />
          </div>
        }
      />
    )
  }

  return (
    <>
      {isUserAdmin ? (
        <>
          <Route path="/impersonation" element={<ImpersonationPage />} />
          <Route path="/logs" lazy={() => import('./pages/UserLogsPage')} />
          <Route
            path="/logs-actividad"
            lazy={() => import('./pages/ActivityLogsPage')}
          />
        </>
      ) : null}

      {isUserOrgManager || isUserAdmin ? (
        <>
          <Route
            path="/knowledge-bases/*"
            element={<KnowledgeLayout knowledgeBaseId={user.organization} />}
          >
            <Route
              path="oauth-callback/:storageProvider"
              element={<KnowledgeBaseOAuthCallback />}
            />
            <Route path=":id/edit" element={<KnowledgeBaseFormPage />} />
            <Route path=":id/spaces/:spaceId" element={<SpaceDetailPage />} />
            <Route path=":id" element={<KnowledgeBaseDetailPage />} />
            <Route
              path=""
              element={
                isUserAdmin ? (
                  <Navigate replace to={`global`} />
                ) : isUserOrgManager ? (
                  <Navigate replace to={`${user.organization}`} />
                ) : (
                  <Navigate replace to="/knowledge-bases" />
                )
              }
            />
          </Route>
        </>
      ) : null}

      {isUserActive ? (
        chatTokensLeft > 0 ? (
          <>
            {/* <Route path="ui-components" element={<UIComponents />} /> */}

            {isUserOrgManager ? (
              <>
                <Route path="/organization/*" element={<OrganizationLayout />}>
                  <Route index element={<Navigate replace to="members" />} />
                  <Route
                    path="members"
                    lazy={() => import('./pages/SubscriptionsPage')}
                  />

                  <Route
                    path="information"
                    lazy={() => import('./pages/CompanyInfoPage')}
                  />
                  <Route
                    path="billing-info"
                    lazy={() => import('./pages/BillingInfoPage')}
                  />

                  <Route
                    path="departments"
                    lazy={() => import('./pages/DepartmentsPage')}
                  >
                    <Route
                      path=":departmentId"
                      lazy={() => import('./pages/DepartmentDetailPage')}
                    />
                  </Route>
                </Route>

                <Route
                  path="/stats"
                  lazy={() => import('./pages/OrgStatsPage')}
                />
              </>
            ) : null}

            <Route path="/" element={<Navigate replace to="/home" />} />

            <Route path="/api" lazy={() => import('./pages/APIDocPage')} />

            <Route
              path="/auth/google-callback"
              lazy={() => import('./pages/auth/GoogleOAuthCallbackPage')}
            />

            <Route
              path="/auth/microsoft-callback"
              lazy={() => import('./pages/auth/MicrosoftOAuthCallbackPage')}
            />

            <Route
              path="/tools/:id/:threadId"
              lazy={() => import('./pages/ToolDetailPage')}
            />
            <Route
              path="/tools/:id"
              lazy={() => import('./pages/ToolDetailPage')}
            />
            {/* NOTE: DEPRECATED ROUTES
            <Route path="/tools" lazy={() => import('./pages/ToolListPage')} />
            <Route
              path="/toolsets/:slug"
              lazy={() => import('./pages/ToolListPage')}
            />
            <Route
              path="/resources"
              lazy={() => import('./pages/ResourcesPage')}
            /> */}
            <Route
              path="/assistants"
              lazy={() => import('./pages/GeneralAssistantPage')}
            />

            <Route
              path="/contents/:slug"
              lazy={() => import('./pages/ContentDetailPage')}
            />
            <Route
              path="/history-images"
              lazy={() => import('./pages/ImageToolHistoryPage')}
            />
            <Route path="/history" lazy={() => import('./pages/HistoryPage')} />
            <Route
              path="/account-settings"
              lazy={() => import('./pages/AccountSettingsPage')}
            />
            <Route path="/home" lazy={() => import('./pages/HomePage')} />
            <Route
              path="/connections"
              lazy={() => import('./pages/ConnectionsPage')}
            />
            <Route path="/explore" lazy={() => import('./pages/ExplorePage')} />
            <Route
              path="/new-explore"
              lazy={() => import('./pages/NewExplorePage')}
            />
            <Route
              path="/new-home"
              lazy={() => import('./pages/HomeAssistantPage')}
            />
            <Route
              path="/explore-test"
              lazy={() => import('./pages/ExploreTestPage')}
            />
            <Route
              path="/projects"
              lazy={() => import('./pages/ProjectsPage')}
            />
            <Route
              path="/projects/:id"
              lazy={() => import('./pages/ProjectsDetailPage')}
            />
            <Route
              path="/use-cases"
              lazy={() => import('./pages/UseCasesPage')}
            />
            <Route
              path="/image/:id"
              lazy={() => import('./pages/ImageToolDetailPage')}
            />
            <Route
              path="/image"
              lazy={() => import('./pages/ImageToolListPage')}
            />
            <Route
              path="/magic-button-playground"
              lazy={() => import('./pages/MagicButtonPlaygroundPage')}
            />
            <Route path="/agents" lazy={() => import('./pages/AgentsPage')} />

            <Route path="/support" lazy={() => import('./pages/SupportPage')} />

            <Route
              path="/responsible-declaration"
              lazy={() => import('./pages/ResponsibleDeclaration')}
            />
            <Route path="/erp">
              <Route
                path="/erp/invoicing"
                element={<ErpEmbed page={ERP_APPS.invoicing} />}
              />
              <Route
                path="/erp/chat"
                element={<ErpEmbed page={ERP_APPS.chat} />}
              />
              <Route
                path="/erp/planner"
                element={<ErpEmbed page={ERP_APPS.planner} />}
              />
              <Route
                path="/erp/crm"
                element={<ErpEmbed page={ERP_APPS.crm} />}
              />
              <Route
                path="/erp/calendar"
                element={<ErpEmbed page={ERP_APPS.calendar} />}
              />
              <Route
                path="/erp/documents"
                element={<ErpEmbed page={ERP_APPS.documents} />}
              />
              <Route
                path="/erp/contacts"
                element={<ErpEmbed page={ERP_APPS.contacts} />}
              />
              <Route
                path="/erp/employees"
                element={<ErpEmbed page={ERP_APPS.employees} />}
              />
              <Route
                path="/erp/point-of-sale"
                element={<ErpEmbed page={ERP_APPS.pointOfSale} />}
              />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </>
        ) : (
          <Route path="*" element={<UserOutOfCreditsPage />} />
        )
      ) : (
        <Route
          path="*"
          element={<UserNotActivePage isManager={isUserOrgManager} />}
        />
      )}
    </>
  )
}
